/**
 * CMS Content Library Zustand store following ADR-021 sliced store with immer.
 *
 * Manages the content library: personal info, career entries (+ variants),
 * skills, education, certificates, and patents.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  PersonalInfoBase,
  PersonalInfoOverride,
  CareerEntry,
  CareerEntryVariant,
  Skill,
  Education,
  Certificate,
  Patent,
} from '../../types/cms.types';
import {
  personalInfoApi,
  careerApi,
  skillsApi,
  educationApi,
  certificatesApi,
  patentsApi,
} from '../../services/cms.api';
import { logger } from '../../services/logger.client';

const log = logger.child({ module: 'contentStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface ContentState {
  personalInfo: PersonalInfoBase | null;
  personalInfoOverrides: PersonalInfoOverride[];
  careerEntries: CareerEntry[];
  /** Map of career entry id -> variants */
  variants: Record<string, CareerEntryVariant[]>;
  skills: Skill[];
  education: Education[];
  certificates: Certificate[];
  patents: Patent[];
  isLoading: boolean;
  error: string | null;
}

interface ContentActions {
  /* ---- Personal Info ---- */
  fetchPersonalInfo: () => Promise<void>;
  updatePersonalInfo: (data: Partial<PersonalInfoBase>) => Promise<void>;
  fetchOverrides: (siteId: string) => Promise<void>;
  upsertOverride: (siteId: string, fieldName: string, fieldValue: string) => Promise<void>;
  deleteOverride: (siteId: string, fieldName: string) => Promise<void>;

  /* ---- Career Entries ---- */
  fetchCareerEntries: () => Promise<void>;
  createCareerEntry: (data: Partial<CareerEntry>) => Promise<CareerEntry | null>;
  updateCareerEntry: (id: string, data: Partial<CareerEntry>) => Promise<void>;
  deleteCareerEntry: (id: string) => Promise<void>;

  /* ---- Variants ---- */
  fetchVariants: (entryId: string) => Promise<void>;
  createVariant: (entryId: string, data: Partial<CareerEntryVariant>) => Promise<CareerEntryVariant | null>;
  updateVariant: (entryId: string, variantId: string, data: Partial<CareerEntryVariant>) => Promise<void>;
  deleteVariant: (entryId: string, variantId: string) => Promise<void>;

  /* ---- Skills ---- */
  fetchSkills: () => Promise<void>;
  createSkill: (data: Partial<Skill>) => Promise<Skill | null>;
  updateSkill: (id: string, data: Partial<Skill>) => Promise<void>;
  deleteSkill: (id: string) => Promise<void>;

  /* ---- Education ---- */
  fetchEducation: () => Promise<void>;
  createEducation: (data: Partial<Education>) => Promise<Education | null>;
  updateEducation: (id: string, data: Partial<Education>) => Promise<void>;
  deleteEducation: (id: string) => Promise<void>;

  /* ---- Certificates ---- */
  fetchCertificates: () => Promise<void>;
  createCertificate: (data: Partial<Certificate>) => Promise<Certificate | null>;
  updateCertificate: (id: string, data: Partial<Certificate>) => Promise<void>;
  deleteCertificate: (id: string) => Promise<void>;

  /* ---- Patents ---- */
  fetchPatents: () => Promise<void>;
  createPatent: (data: Partial<Patent>) => Promise<Patent | null>;
  updatePatent: (id: string, data: Partial<Patent>) => Promise<void>;
  deletePatent: (id: string) => Promise<void>;

  clearError: () => void;
}

export type ContentStore = ContentState & { actions: ContentActions };

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useContentStore = create<ContentStore>()(
  immer((set) => ({
    /* ---------- initial state ---------- */
    personalInfo: null,
    personalInfoOverrides: [],
    careerEntries: [],
    variants: {},
    skills: [],
    education: [],
    certificates: [],
    patents: [],
    isLoading: false,
    error: null,

    /* ---------- actions ---------- */
    actions: {
      /* ---- Personal Info ---- */
      fetchPersonalInfo: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          log.info('Fetching personal info');
          const info = await personalInfoApi.getBase();
          set((state) => { state.personalInfo = info; state.isLoading = false; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch personal info');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      updatePersonalInfo: async (data: Partial<PersonalInfoBase>) => {
        set((state) => { state.error = null; });
        try {
          log.info('Updating personal info');
          const updated = await personalInfoApi.updateBase(data);
          set((state) => { state.personalInfo = updated; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update personal info');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      fetchOverrides: async (siteId: string) => {
        try {
          log.info('Fetching overrides', { siteId });
          const overrides = await personalInfoApi.getOverrides(siteId);
          set((state) => { state.personalInfoOverrides = overrides; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch overrides');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      upsertOverride: async (siteId: string, fieldName: string, fieldValue: string) => {
        try {
          log.info('Upserting override', { siteId, fieldName });
          const override = await personalInfoApi.upsertOverride(siteId, fieldName, fieldValue);
          set((state) => {
            const idx = state.personalInfoOverrides.findIndex(
              (o) => o.field_name === fieldName,
            );
            if (idx !== -1) {
              state.personalInfoOverrides[idx] = override;
            } else {
              state.personalInfoOverrides.push(override);
            }
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to upsert override');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteOverride: async (siteId: string, fieldName: string) => {
        try {
          log.info('Deleting override', { siteId, fieldName });
          await personalInfoApi.deleteOverride(siteId, fieldName);
          set((state) => {
            state.personalInfoOverrides = state.personalInfoOverrides.filter(
              (o) => o.field_name !== fieldName,
            );
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete override');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Career Entries ---- */
      fetchCareerEntries: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          log.info('Fetching career entries');
          const entries = await careerApi.list();
          set((state) => { state.careerEntries = entries; state.isLoading = false; });
          log.info('Career entries fetched', { count: entries.length });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch career entries');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      createCareerEntry: async (data: Partial<CareerEntry>) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          log.info('Creating career entry');
          const entry = await careerApi.create(data);
          set((state) => { state.careerEntries.push(entry); state.isLoading = false; });
          log.info('Career entry created', { id: entry.id });
          return entry;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create career entry');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
          return null;
        }
      },

      updateCareerEntry: async (id: string, data: Partial<CareerEntry>) => {
        set((state) => { state.error = null; });
        try {
          log.info('Updating career entry', { id });
          const updated = await careerApi.update(id, data);
          set((state) => {
            const idx = state.careerEntries.findIndex((e) => e.id === id);
            if (idx !== -1) state.careerEntries[idx] = updated;
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update career entry');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteCareerEntry: async (id: string) => {
        set((state) => { state.error = null; });
        try {
          log.info('Deleting career entry', { id });
          await careerApi.delete(id);
          set((state) => {
            state.careerEntries = state.careerEntries.filter((e) => e.id !== id);
            delete state.variants[id];
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete career entry');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Variants ---- */
      fetchVariants: async (entryId: string) => {
        try {
          log.info('Fetching variants', { entryId });
          const variants = await careerApi.listVariants(entryId);
          set((state) => { state.variants[entryId] = variants; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch variants');
          log.error(msg);
          /* Set to empty array (not undefined) so the caller's guard
             `!variants[entry.id]` won't re-trigger fetching indefinitely
             on subsequent effect runs. */
          set((state) => { state.variants[entryId] = []; state.error = msg; });
        }
      },

      createVariant: async (entryId: string, data: Partial<CareerEntryVariant>) => {
        try {
          log.info('Creating variant', { entryId });
          const variant = await careerApi.createVariant(entryId, data);
          set((state) => {
            if (!state.variants[entryId]) state.variants[entryId] = [];
            state.variants[entryId]!.push(variant);
          });
          return variant;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create variant');
          log.error(msg);
          set((state) => { state.error = msg; });
          return null;
        }
      },

      updateVariant: async (entryId: string, variantId: string, data: Partial<CareerEntryVariant>) => {
        try {
          log.info('Updating variant', { entryId, variantId });
          const updated = await careerApi.updateVariant(entryId, variantId, data);
          set((state) => {
            const variants = state.variants[entryId];
            if (variants) {
              const idx = variants.findIndex((v) => v.id === variantId);
              if (idx !== -1) variants[idx] = updated;
            }
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update variant');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteVariant: async (entryId: string, variantId: string) => {
        try {
          log.info('Deleting variant', { entryId, variantId });
          await careerApi.deleteVariant(entryId, variantId);
          set((state) => {
            const variants = state.variants[entryId];
            if (variants) {
              state.variants[entryId] = variants.filter((v) => v.id !== variantId);
            }
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete variant');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Skills ---- */
      fetchSkills: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          log.info('Fetching skills');
          const skills = await skillsApi.list();
          set((state) => { state.skills = skills; state.isLoading = false; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch skills');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      createSkill: async (data: Partial<Skill>) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const skill = await skillsApi.create(data);
          set((state) => { state.skills.push(skill); state.isLoading = false; });
          return skill;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create skill');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
          return null;
        }
      },

      updateSkill: async (id: string, data: Partial<Skill>) => {
        set((state) => { state.error = null; });
        try {
          const updated = await skillsApi.update(id, data);
          set((state) => {
            const idx = state.skills.findIndex((s) => s.id === id);
            if (idx !== -1) state.skills[idx] = updated;
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update skill');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteSkill: async (id: string) => {
        set((state) => { state.error = null; });
        try {
          await skillsApi.delete(id);
          set((state) => { state.skills = state.skills.filter((s) => s.id !== id); });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete skill');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Education ---- */
      fetchEducation: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const items = await educationApi.list();
          set((state) => { state.education = items; state.isLoading = false; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch education');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      createEducation: async (data: Partial<Education>) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const item = await educationApi.create(data);
          set((state) => { state.education.push(item); state.isLoading = false; });
          return item;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create education');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
          return null;
        }
      },

      updateEducation: async (id: string, data: Partial<Education>) => {
        set((state) => { state.error = null; });
        try {
          const updated = await educationApi.update(id, data);
          set((state) => {
            const idx = state.education.findIndex((e) => e.id === id);
            if (idx !== -1) state.education[idx] = updated;
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update education');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteEducation: async (id: string) => {
        set((state) => { state.error = null; });
        try {
          await educationApi.delete(id);
          set((state) => { state.education = state.education.filter((e) => e.id !== id); });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete education');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Certificates ---- */
      fetchCertificates: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const items = await certificatesApi.list();
          set((state) => { state.certificates = items; state.isLoading = false; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch certificates');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      createCertificate: async (data: Partial<Certificate>) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const item = await certificatesApi.create(data);
          set((state) => { state.certificates.push(item); state.isLoading = false; });
          return item;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create certificate');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
          return null;
        }
      },

      updateCertificate: async (id: string, data: Partial<Certificate>) => {
        set((state) => { state.error = null; });
        try {
          const updated = await certificatesApi.update(id, data);
          set((state) => {
            const idx = state.certificates.findIndex((c) => c.id === id);
            if (idx !== -1) state.certificates[idx] = updated;
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update certificate');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deleteCertificate: async (id: string) => {
        set((state) => { state.error = null; });
        try {
          await certificatesApi.delete(id);
          set((state) => { state.certificates = state.certificates.filter((c) => c.id !== id); });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete certificate');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      /* ---- Patents ---- */
      fetchPatents: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const items = await patentsApi.list();
          set((state) => { state.patents = items; state.isLoading = false; });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to fetch patents');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
        }
      },

      createPatent: async (data: Partial<Patent>) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const item = await patentsApi.create(data);
          set((state) => { state.patents.push(item); state.isLoading = false; });
          return item;
        } catch (err) {
          const msg = errorMessage(err, 'Failed to create patent');
          log.error(msg);
          set((state) => { state.isLoading = false; state.error = msg; });
          return null;
        }
      },

      updatePatent: async (id: string, data: Partial<Patent>) => {
        set((state) => { state.error = null; });
        try {
          const updated = await patentsApi.update(id, data);
          set((state) => {
            const idx = state.patents.findIndex((p) => p.id === id);
            if (idx !== -1) state.patents[idx] = updated;
          });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to update patent');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      deletePatent: async (id: string) => {
        set((state) => { state.error = null; });
        try {
          await patentsApi.delete(id);
          set((state) => { state.patents = state.patents.filter((p) => p.id !== id); });
        } catch (err) {
          const msg = errorMessage(err, 'Failed to delete patent');
          log.error(msg);
          set((state) => { state.error = msg; });
        }
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },
    },
  })),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks (ADR-021 wrapper-hooks pattern)                    */
/* ------------------------------------------------------------------ */

/** Stable reference to actions -- never causes a re-render. */
export function useContentActions() {
  return useContentStore((s) => s.actions);
}
